import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clientsApi, casesApi, FISCAL_YEAR_NOW, CLIENT_TYPE_LABEL, type Case, type ClientType } from "../api.js";
import { Modal } from "../components/Modal.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { toast } from "../components/Toast.js";
import { btnStyle } from "../App.js";
import { useScreenView } from "../lib/analytics.js";

export function ClientDetailPage() {
  useScreenView("client_detail");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [caseModal, setCaseModal] = useState(false);
  const [caseForm, setCaseForm] = useState({ fiscal_year: FISCAL_YEAR_NOW, title: "", notes: "" });

  const { data: client, isLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: () => clientsApi.get(id!),
    enabled: !!id,
  });

  const { data: casesData } = useQuery({
    queryKey: ["cases", { client_id: id }],
    queryFn: () => casesApi.list({ client_id: id }),
    enabled: !!id,
  });

  const createCaseMut = useMutation({
    mutationFn: () => casesApi.create({ client_id: id!, ...caseForm }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["cases"] });
      setCaseModal(false);
      toast("案件を作成しました");
      navigate(`/cases/${c.id}`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  if (isLoading) return <div style={{ padding: 24, color: "#94a3b8" }}>読み込み中…</div>;
  if (!client)   return <div style={{ padding: 24, color: "#dc2626" }}>顧客が見つかりません</div>;

  const cases: Case[] = casesData?.data ?? [];

  return (
    <div style={{ padding: 24 }}>
      {/* パンくず */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
        <Link to="/clients" style={{ color: "#0f766e", textDecoration: "none" }}>顧客管理</Link>
        {" › "}{client.name}
      </div>

      {/* 顧客情報 */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{client.name}</h1>
          <span style={{
            display: "inline-block", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: client.client_type === "corporation" ? "#eff6ff" : "#f0fdf4",
            color:      client.client_type === "corporation" ? "#1d4ed8" : "#16a34a",
          }}>
            {CLIENT_TYPE_LABEL[(client.client_type ?? "individual") as ClientType]}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, color: "#475569" }}>
          {[
            ["郵便番号", client.postal_code],
            ["住所",     client.address],
            ["電話",     client.tel],
            ["備考",     client.notes],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label as string}>
              <span style={{ fontWeight: 600 }}>{label}：</span>{value}
            </div>
          ))}
        </div>
      </div>

      {/* 案件一覧 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>案件一覧</h2>
        <button style={btnStyle("primary")} onClick={() => setCaseModal(true)}>+ 案件を作成</button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
            {["年度", "案件名", "ステータス", "作成日", ""].map((h) => (
              <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cases.map((c) => (
            <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "10px 12px", color: "#64748b", whiteSpace: "nowrap" }}>令和{c.fiscal_year}年</td>
              <td style={{ padding: "10px 12px", fontWeight: 500 }}>{c.title ?? "—"}</td>
              <td style={{ padding: "10px 12px" }}><StatusBadge status={c.status} /></td>
              <td style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>
                {new Date(c.created_at).toLocaleDateString("ja-JP")}
              </td>
              <td style={{ padding: "10px 12px", textAlign: "right" }}>
                <button style={btnStyle("outline")} onClick={() => navigate(`/cases/${c.id}`)}>開く</button>
              </td>
            </tr>
          ))}
          {cases.length === 0 && (
            <tr><td colSpan={5} style={{ padding: "24px 12px", textAlign: "center", color: "#94a3b8" }}>案件がありません</td></tr>
          )}
        </tbody>
      </table>

      {caseModal && (
        <Modal title="案件を作成" onClose={() => setCaseModal(false)}>
          <form onSubmit={(e) => { e.preventDefault(); createCaseMut.mutate(); }}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>年度（西暦下2桁）</span>
              <input type="number" min={0} max={99} value={caseForm.fiscal_year}
                onChange={(e) => setCaseForm((f) => ({ ...f, fiscal_year: Number(e.target.value) }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14, width: 120 }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>案件名</span>
              <input type="text" value={caseForm.title} placeholder="例：相続登記、所有権移転など"
                onChange={(e) => setCaseForm((f) => ({ ...f, title: e.target.value }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>備考</span>
              <input type="text" value={caseForm.notes}
                onChange={(e) => setCaseForm((f) => ({ ...f, notes: e.target.value }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => setCaseModal(false)}>キャンセル</button>
              <button type="submit" style={btnStyle("primary")} disabled={createCaseMut.isPending}>作成する</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
